/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Pagination from "@mui/material/Pagination";
import Grid from "@mui/material/Grid";
import Avatar from "@mui/material/Avatar";
import InputAdornment from "@mui/material/InputAdornment";
import Alert from "@mui/material/Alert";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import TableHeader from "components/Table/TableHeader";
import MDDateRange from "components/MDDateRange";

// Material Dashboard 2 PRO React examples
import { useTranslation } from "react-i18next";
import { APP_STATE } from "services/Define";
import { FetchData } from "services/Request";
import { useSelector } from "react-redux";
import * as Func from "services/Function";

const initFind = {
  search: "",
  order_by: "tb.created_at",
  order_sort: "asc",
  page: 1,
  perPage: 10,
  pageTotal: 0,
  period: "ALL",
  entity: "ALL",
  ref_id: "",
  date_start: Func.dateTimeFirstMonth(),
  date_stop: Func.dateTimeEndMonth(),
};

const initSummary = {
  quantity: "",
};

function RpStockBranchCard({ ref_id }) {
  const mod = "rp_stock_branch_card";
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [summary, setSummary] = useState(initSummary);
  const cboEntity = [
    { id: "ALL", name: t(`element.show_all`) },
    { id: "receive", name: t(`menu.receive`) },
    { id: "export_store", name: t(`menu.export_store`) },
  ];

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: "tb.created_at", label: "วันทำรายการ", align: "center" },
    { id: "pd.image_id", label: t(`attr.image`), align: "center" },
    { id: "pd.sku", label: "SKU", align: "left" },
    { id: "pd.name", label: "ชื่อสินค้า", align: "left" },
    { id: "tb.stock_mode", label: "สถานะ", align: "center" },
    { id: "tb.entity", label: "กระบวนการ", align: "left" },
    { id: "tb.ref_code", label: "เลขที่บิล", align: "left" },
    { id: "tb.quantity", label: "ปริมาณ", align: "right" },
    { id: "tb.unit_id", label: "หน่วยนับ", align: "left" },
    { id: "tb.quantity_before", label: "ก่อนเปลี่ยน", align: "right" },
    { id: "tb.quantity_after", label: "หลังเปลี่ยน", align: "right" },
  ];

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData("POST", `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      entity: data.entity,
      period: data.period,
      ref_id: ref_id,
      date_start: data.date_start,
      date_stop: data.date_stop,
    }).then((result) => {
      if (result.status === "ok") {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);

        setSummary(result.summary);
      }
      setLoading(false);
    });
  };

  const setDateRange = (start, stop) => {
    view({
      ...find,
      date_start: Func.dateTimeFormat(start.toDate()),
      date_stop: Func.dateTimeFormat(stop.toDate()),
      page: 1,
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? "desc" : "asc" });
  };

  const imageDisplay = (item) => {
    return <Avatar alt={item.product_name} src={item.image} variant="rounded" />;
  };

  const stockModeDisplay = (inp) => {
    if (inp === "IN")
      return (
        <Alert
          icon={<Icon>file_download</Icon>}
          severity="success"
          sx={{
            fontSize: "14px",
            padding: "0 10px",
            fontWeight: 400,
            borderRadius: 5,
            whiteSpace: "nowrap",
          }}
        >
          รับเข้า
        </Alert>
      );

    return (
      <Alert
        icon={<Icon>file_upload</Icon>}
        severity="error"
        sx={{
          fontSize: "14px",
          padding: "0 10px",
          fontWeight: 400,
          borderRadius: 5,
          whiteSpace: "nowrap",
        }}
      >
        นำออก
      </Alert>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12} md={4} lg={3}>
                  <MDBox
                    noValidate
                    component="form"
                    role="form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      view({ ...find, period: "ALL", page: 1 });
                    }}
                  >
                    <MDInput
                      name="searchform"
                      fullWidth
                      value={find.search}
                      type="search"
                      onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <span>เลขที่บิล :</span>
                          </InputAdornment>
                        ),
                        autoComplete: "off",
                      }}
                    />
                  </MDBox>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <MDInput
                    fullWidth
                    value={find.entity}
                    defaultValue="ALL"
                    select
                    onChange={(e) => view({ ...find, entity: e.target.value, page: 1 })}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <span>เมนู :</span>
                        </InputAdornment>
                      ),
                    }}
                  >
                    {cboEntity.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </MDInput>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <MDInput
                    fullWidth
                    value={find.period}
                    defaultValue="ALL"
                    select
                    onChange={(e) => view({ ...find, period: e.target.value, page: 1 })}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">{t("element.period")} :</InputAdornment>
                      ),
                    }}
                  >
                    <MenuItem value="ALL">ทั้งหมด</MenuItem>
                    <MenuItem value="DATE">ช่วงเวลา</MenuItem>
                  </MDInput>
                </Grid>
                {find.period === "ALL" ? null : (
                  <Grid item xs={12} sm={12} md={12} lg={5}>
                    <MDDateRange
                      dateStart={find.date_start}
                      dateStop={find.date_stop}
                      callBack={setDateRange}
                    />
                  </Grid>
                )}
              </Grid>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 500 }}>
              <Table aria-label="simple table">
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell align="center">{Func.dateTimeDisplay(row.created_at)}</TableCell>
                      <TableCell width="40">{imageDisplay(row)}</TableCell>
                      <TableCell align="left">{row.product_sku}</TableCell>
                      <TableCell align="left">{row.product_name}</TableCell>
                      <TableCell align="center">{stockModeDisplay(row.stock_mode)}</TableCell>
                      <TableCell align="left">{t(`entity.${row.entity}`)}</TableCell>
                      <TableCell align="center">{row.ref_code}</TableCell>
                      <TableCell align="right">{Func.numberDisplay(row.quantity)}</TableCell>
                      <TableCell align="left">{row.unit_name}</TableCell>
                      <TableCell align="right">{Func.numberDisplay(row.quantity_before)}</TableCell>
                      <TableCell align="right">{Func.numberDisplay(row.quantity_after)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display="flex" justifyContent="space-between" alignItems="flex-start">
                <MDButton variant={show ? "contained" : "outlined"} color="dark" onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant="outlined"
                  color="secondary"
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    view(find);
  }, [ref_id]);

  return (
    <div>
      {renderList()}
      {renderLoading()}
    </div>
  );
}

RpStockBranchCard.propTypes = {
  ref_id: PropTypes.string.isRequired,
};

export default RpStockBranchCard;
